import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Users, FileWarning, ShieldCheck, DollarSign, Clock, ArrowRight, TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_COLORS } from "@/lib/brand";
import { Loader2 } from "lucide-react";

export default function Dashboard({ goTo }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats)
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#C1121F]" /></div>;

  const tiles = [
    { label: "Consumers", value: stats.consumers, icon: Users, accent: "#C1121F" },
    { label: "Active Disputes", value: stats.activeDisputes, icon: FileWarning, accent: "#F59E0B" },
    { label: "Blocked / Resolved", value: stats.resolvedDisputes, icon: ShieldCheck, accent: "#15803D" },
    { label: "Total Disputed", value: stats.totalDisputed, icon: DollarSign, accent: "#60A5FA" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-5" data-testid="metric-stat-tile">
            <div className="flex items-center justify-between">
              <span className="inline-grid place-items-center h-10 w-10 rounded-xl" style={{ backgroundColor: `${t.accent}1f` }}>
                <t.icon className="h-5 w-5" style={{ color: t.accent }} />
              </span>
            </div>
            <div className="mt-4 font-display text-3xl font-extrabold">{t.value}</div>
            <div className="text-sm text-neutral-500">{t.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recovered */}
        <div className="rounded-2xl border border-[#15803D]/30 bg-[#15803D]/[0.06] p-6">
          <div className="flex items-center gap-2 text-[#15803D]"><TrendingUp className="h-5 w-5" /><span className="font-semibold">Value Recovered / Cleared</span></div>
          <div className="mt-4 font-display text-4xl font-extrabold text-neutral-900">{stats.recovered}</div>
          <p className="mt-2 text-sm text-neutral-500">Total balances on blocked & deleted tradelines.</p>
          <button onClick={() => goTo("tracking")} className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#C1121F] hover:text-[#9B0E18]">
            View tracking <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Deadlines */}
        <div className="lg:col-span-2 rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <div className="flex items-center gap-2 text-amber-700"><Clock className="h-5 w-5" /><span className="font-semibold">Statutory Deadlines (4-day SLA)</span></div>
          <div className="mt-4 space-y-2">
            {stats.pendingDeadlines.length === 0 && <p className="text-sm text-neutral-400">No pending deadlines. All caught up.</p>}
            {stats.pendingDeadlines.map((d) => {
              const days = Math.ceil((new Date(d.deadline) - new Date()) / 86400000);
              const overdue = days < 0;
              return (
                <div key={d.id} className="flex items-center justify-between rounded-xl bg-neutral-50 border border-neutral-200 px-4 py-3">
                  <div>
                    <div className="text-sm font-medium">{d.consumerName}</div>
                    <div className="text-xs text-neutral-400">{d.bureau} · {d.status}</div>
                  </div>
                  <div className={`text-xs font-mono px-2.5 py-1 rounded-md border ${overdue ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {overdue ? `${Math.abs(days)}d overdue` : `${days}d left`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <div className="font-semibold mb-4">By Status</div>
          <div className="space-y-2">
            {Object.entries(stats.byStatus).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className={`px-2.5 py-1 rounded-md border text-xs ${STATUS_COLORS[k] || "bg-stone-100 text-stone-600 border-stone-300"}`}>{k}</span>
                <span className="font-mono text-neutral-600">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
          <div className="font-semibold mb-4">By Bureau</div>
          <div className="space-y-3">
            {Object.entries(stats.byBureau).map(([k, v]) => {
              const max = Math.max(...Object.values(stats.byBureau), 1);
              return (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="font-mono text-neutral-500">{v}</span></div>
                  <div className="h-2 rounded-full bg-neutral-50 overflow-hidden">
                    <div className="h-full rounded-full bg-[#C1121F]" style={{ width: `${(v / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
